import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { mdiChat } from '@mdi/js';
import MdiIcon from '../mdi/MdiIcon';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { ChatMessage } from './ChatMessage';
import { ChatSpinner } from './ChatSpinner';
import { usePrefersReducedMotion } from '../../../hooks/usePrefersReducedMotion';
import type { ChatMessage as ChatMessageData, ChatFile } from './types';

/**
 * Openers shown on an empty conversation; each maps to a tool the model has.
 *
 * Translated, because they are prompts the user reads. The model is asked in
 * English whatever the interface language is, which is a separate decision --
 * the backend's persona is locale-aware and says so.
 */
const starterPrompts = () => [
  t`Show the last 10 shipments`,
  t`Show the last 10 logs`,
  t`Give me a report for the last month for each active cutting`,
  t`Give me a report for the last month for each contractor`,
];

/**
 * How far from the bottom, in px, still counts as "parked at the bottom".
 *
 * Below this the user is reading back through the thread, so appending a
 * streamed token must not drag the view down.
 */
const FOLLOW_THRESHOLD_PX = 80;

export interface ChatThreadHandle {
  /**
   * Re-attach the view to the bottom of the thread.
   *
   * Called when the user sends something: whatever they had scrolled to, a new
   * message is what they want to see.
   */
  follow(): void;
}

export interface ChatThreadProps {
  messages: ChatMessageData[];
  isLoading: boolean;
  /** Sends a starter prompt from the empty state. */
  onSendPrompt: (prompt: string) => void;
  onEdit: (messageId: string, content: string) => void;
  onRegenerate: () => void;
  /** Called when the user asks for one of a reply's files. */
  onDownloadFile?: (file: ChatFile) => void;
}

/**
 * The scrolling message list, the empty state and the "Thinking..." row.
 *
 * Split out of `ChatWidget` so that the scroll-following behaviour can be
 * exercised without a drawer, a store or an open panel. It owns no data: every
 * value arrives as a prop, and the only thing it keeps is where the user is
 * scrolled to.
 */
export const ChatThread = forwardRef<ChatThreadHandle, ChatThreadProps>(
  function ChatThread(
    { messages, isLoading, onSendPrompt, onEdit, onRegenerate, onDownloadFile },
    ref,
  ) {
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    // Whether to keep yanking the view down as tokens arrive. Ref rather than
    // state: it is read on every scroll event and must not itself trigger a
    // render.
    const followStreamRef = useRef(true);

    const reducedMotion = usePrefersReducedMotion();

    const scrollToEnd = useCallback(() => {
      // 'smooth' animates the viewport on every streamed token. Where motion is
      // reduced the jump is instant, which lands in the same place.
      messagesEndRef.current?.scrollIntoView({
        behavior: reducedMotion ? 'auto' : 'smooth',
        block: 'end',
      });
    }, [reducedMotion]);

    // `onEdit` and `onRegenerate` come from a hook that recreates them whenever
    // `messages` changes -- which is on every streamed token. Passed straight
    // through they would give every bubble fresh props and defeat the `memo` in
    // ChatMessage, so they are held in refs and the props handed down stay
    // identity-stable for the life of the thread. The ref pattern for event
    // handlers is the documented way to do this.
    const onEditRef = useRef(onEdit);
    const onRegenerateRef = useRef(onRegenerate);
    onEditRef.current = onEdit;
    onRegenerateRef.current = onRegenerate;

    // The same treatment for the download handler: it comes from a hook
    // that recreates it whenever the messages change, and passing it straight
    // through would give every bubble fresh props on every streamed token.
    const onDownloadFileRef = useRef(onDownloadFile);
    onDownloadFileRef.current = onDownloadFile;

    const stableEdit = useCallback(
      (messageId: string, content: string) => onEditRef.current(messageId, content),
      [],
    );
    const stableRegenerate = useCallback(() => onRegenerateRef.current(), []);
    const stableDownload = useCallback(
      (file: ChatFile) => onDownloadFileRef.current?.(file),
      [],
    );

    const follow = useCallback(() => {
      followStreamRef.current = true;
      // Scroll now as well as re-attaching for subsequent tokens. Waiting for
      // the next `messages` change leaves the viewport wherever the user was,
      // which is the one thing sending a message is meant to undo.
      scrollToEnd();
    }, [scrollToEnd]);

    useImperativeHandle(ref, () => ({ follow }), [follow]);

    const handleScroll = () => {
      const el = scrollContainerRef.current;
      if (!el) return;
      const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
      followStreamRef.current = distance < FOLLOW_THRESHOLD_PX;
    };

    useEffect(() => {
      if (followStreamRef.current) {
        scrollToEnd();
      }
    }, [messages, scrollToEnd]);

    return (
      <Box
        ref={scrollContainerRef}
        onScroll={handleScroll}
        data-testid="chat-message-area"
        // `role="log"` is a live region built for exactly this: a transcript
        // that grows. `aria-relevant="additions"` stops it re-announcing the
        // whole thread every time a token lands, and `aria-busy` holds the
        // announcement back while a reply streams so it is read once at the
        // end rather than word by word.
        role="log"
        aria-live="polite"
        aria-relevant="additions text"
        aria-busy={isLoading}
        aria-label={t`Conversation`}
        sx={{
          flex: 1,
          overflow: 'auto',
          // No horizontal padding: the bubbles measure their 90% against
          // the full width of the message area.
          py: 1,
          // Was hardcoded to grey.50, which left a bright panel inside the dark
          // dialog. The scheme now decides which near-neutral this is.
          backgroundColor: (theme) => theme.palette.chat?.canvas,
        }}
      >
        {messages.length === 0 && (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              color: 'text.secondary',
            }}
          >
            <MdiIcon d={mdiChat} size={48} />
            <Typography variant="body2" sx={{ mt: 2 }}>
              <Trans>Start a conversation with the AI assistant</Trans>
            </Typography>
            <Typography variant="caption" sx={{ mt: 1 }}>
              <Trans>Ask about logs, shipments, buyers, or other timber data</Trans>
            </Typography>
            <Box
              sx={{
                mt: 2,
                maxWidth: 380,
                px: 2,
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                justifyContent: 'center',
              }}
            >
              {starterPrompts().map((prompt) => (
                <Chip
                  key={prompt}
                  label={prompt}
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    follow();
                    onSendPrompt(prompt);
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {messages.map((msg) => (
          <ChatMessage
            key={msg.id}
            id={msg.id}
            role={msg.role}
            content={msg.content}
            toolCalls={msg.toolCalls}
            createdAt={msg.created_at}
            complete={msg.complete}
            busy={isLoading}
            onEdit={stableEdit}
            onRegenerate={stableRegenerate}
            onDownloadFile={stableDownload}
          />
        ))}

        {isLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2 }}>
            <ChatSpinner size={16} />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              <Trans>Thinking...</Trans>
            </Typography>
          </Box>
        )}

        <div ref={messagesEndRef} />
      </Box>
    );
  },
);
